import { useState, type FormEvent } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { Alert, Box, Button, Link, Stack } from '@mui/material';
import { PASSWORD_MIN_LENGTH } from '@pd/shared';
import { api, ApiError } from '../../api/client';
import { t } from '../../i18n/sq';
import { PasswordField } from '../../components/PasswordField';
import { AuthShell } from './AuthShell';

/** Handles both "forgot password" links and first-time account invitations (?ftese=1). */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const isInvite = params.get('ftese') === '1';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const mismatch = confirm.length > 0 && confirm !== password;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (mismatch) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.common.unexpectedError);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title={isInvite ? t.auth.inviteTitle : t.auth.resetTitle}
      subtitle={isInvite ? t.auth.inviteSubtitle : undefined}
    >
      <Box component="form" onSubmit={submit}>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}
          {done ? (
            <Alert severity="success">{t.auth.resetDone}</Alert>
          ) : (
            <>
              <PasswordField
                label={t.auth.newPassword}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                helperText={t.auth.passwordHint}
                required
                autoFocus
              />
              <PasswordField
                label={t.auth.confirmPassword}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                error={mismatch}
                helperText={mismatch ? t.auth.passwordsDontMatch : undefined}
                required
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                loading={busy}
                disabled={password.length < PASSWORD_MIN_LENGTH || mismatch || !token}
              >
                {t.common.save}
              </Button>
            </>
          )}
          <Link component={RouterLink} to="/hyr" sx={{ textAlign: 'center' }}>
            {t.auth.backToLogin}
          </Link>
        </Stack>
      </Box>
    </AuthShell>
  );
}
